import React, { useEffect, useRef, useState, useLayoutEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

import ListView from './ListView'
import EventWrapper from './EventWrapper'
import Toolbar from './Toolbar'
import MONTHS from './MONTHS'
import {
  setCalendarLastDate,
  setCalendarViewType,
} from '../../../../redux/events/actions'

/**
 * get the start month for the given start date or center month given a range of two dates
 * @param {Date} date the start date to get the month for
 * @param {Date?} stopDate optional stop date to use to get the middle month for a range
 * @returns a month from MONTHS in form {key, start, end, name, abbr}
 */
const getMonthForDate = (date, stopDate) => {
  const momentDate = moment(date)
  const momentStopDate = moment(stopDate)

  // if there is a stop date we use the center of the two dates otherwise just the start date
  const centerDate = stopDate ? ((momentDate.valueOf() + momentStopDate.valueOf()) / 2) : date
  const startMonth = moment(centerDate).startOf('month').format('MMMM').toUpperCase()

  return MONTHS[startMonth]
}

const localizer = momentLocalizer(moment)

/**
 * Take an object keyed by eventID each containing an event and convert that event into the 
 * structure used by ReactBigCalendar component
 * @param {Object} events Object keyed by events to get calendar formatted events from
 * @returns {Object[]} array of events structured in the format ReactBigCalendar requires
 */ 
const getCalendarEvents = (events) => {
  return Object.values(events).map((event) => {
    let end;

    if(event.autoStopTime) {
      end = moment(event.autoStopTime).toDate()
    } else if (event.estRuntime){
      end = moment(event.startDateTime).add(event.estRuntime, 'minutes').toDate();
    } else {
      end = moment(event.startDateTime).toDate()
    }

    
    const calendarEvent = {
      id: event.eventID,
      status: event.eventStatus,
      data: event,
      title: event.title,
      start: moment(event.startDateTime).toDate(),
      end,
    }
    return calendarEvent
  })
}

const MAX_PER_CALENDAR_PAGE = 500

const unmountEffect = (filters, onFilterChange) => {
  const filterCopy = {
    ...filters
  }
  if (filterCopy.startDateTime) {
    filterCopy.startDateTime = moment(filters.startDateTime)
  }
  if (filterCopy.stopDateTime) {
    filterCopy.stopDateTime = moment(filters.stopDateTime)
  }
  onFilterChange({
    type: 'change',
    params: {
      ...filterCopy
    }
  })
}

/**
 * on load change the filters to contain days that will be shown in the calendar views
 * @param {Object} props
 * @property {Object} props.filters used to get filters.startDateTime and filters.stopDateTime
 * @property {function} props.setMonth sets the month
 * @property {int} props.year the year to base dates off of
 * @property {function} props.onFilterChange callback for filter changes
 */
const useOnLoadEffect = ({ filters, setMonth, year, onFilterChange, currentView }) => {
  useEffect(() => {
    // on first render change filters to the month of the start date
    const monthDate = getMonthForDate(
      filters.startDateTime,
      filters.stopDateTime
    )
    setMonth(monthDate)
    if (
      moment(filters.startDateTime).diff(monthDate.start(year)) !== 0 ||
      moment(filters.stopDateTime).diff(monthDate.end(year)) !== 0
    ) {
      onFilterChange({
        type: 'change',
        params: {
          startDateTime: monthDate.start(year).subtract(1, 'week'),
          stopDateTime: monthDate.end(year).add(1, 'week'),
          resultMax: MAX_PER_CALENDAR_PAGE,
        },
      })
    }
  }, [])
  useLayoutEffect(() => {
    return () => {
      if (currentView === 'agenda') {
        unmountEffect(filters, onFilterChange)
      }
    }
  }, [])
}

/**
 * This effect will give the calendar view styles so that it uses the available height
 * and is positioned appropriately from the top of the screen
 * @param {Object} props 
 * @property {Object} props.calendarRef ref that targets the calendar
 * @property {Object} props.offsetHeightStyles styles that are applied after using the height effect
 * @property {function} props.setOffsetHeightStyles function to apply styles to the calendar
 */
const useHeightEffect = ({calendarRef, offsetHeightStyles, setOffsetHeightStyles}) => {
  // get the top of calendar and set the height to use the remaining vertical space
  useLayoutEffect(() => {
    if (calendarRef && calendarRef.current && !offsetHeightStyles) {
      const top = `${calendarRef.current.offsetTop - 1}px`
      const bottomPadding = '0.5em'
      const margin = '2em'
      setOffsetHeightStyles({
        height: `calc(100vh - ${top} - ${bottomPadding} - ${margin})`,
        minHeight: '35em',
        top: `${top}`,
      })
    }
  })
}

/**
 * 
 * @returns {Object} properties needed from the redux store
 */
const useCalendarSelectors = () => {
  const eventState = useSelector((state) => state.events)
  const view = eventState.calendarViewType || 'month'
  const filters = useSelector((state) => state.events.filters)
  const year = moment((moment(filters.stopDateTime, 'YYYY-MM-DD').toDate().getTime() + moment(filters.startDateTime).toDate().getTime()) / 2).year()
  const [month, setMonth] = useState(
    getMonthForDate(
      filters.startDateTime,
      filters.stopDateTime
    )
  )
  const stateLastDate =
    eventState.calendarLastDate || month.start(year).toDate()

  const [offsetHeightStyles, setOffsetHeightStyles] = useState(null)
  const [currentView, setCurrentView] = useState(view)
  const [lastDate, setLastDate] = useState(stateLastDate)
  return { year, filters, eventState, month, setMonth, offsetHeightStyles, setOffsetHeightStyles, currentView, setCurrentView, lastDate, setLastDate }
}

const CalendarView = (props) => {
  const { events, onFilterChange, toggleActiveEvent, loading } = props
  const [didFirstRender, setDidFirstRender] = useState(false)
  const calendarRef = useRef(null)
  const dispatch = useDispatch()
  useEffect(() => {
    if (!didFirstRender) {
      setDidFirstRender(true)   
    }
  })

  const { year, filters, setMonth, offsetHeightStyles, setOffsetHeightStyles, currentView, setCurrentView, lastDate, setLastDate } = useCalendarSelectors()

  const calendarEvents = getCalendarEvents(events)

  useOnLoadEffect({ filters, setMonth, year, onFilterChange, currentView })
  useHeightEffect({ calendarRef, offsetHeightStyles, setOffsetHeightStyles })

  const handleNavigation = (date) => {
    const isInRange = moment(date).isBetween(
      moment(filters.startDateTime).add(1, 'week'),
      moment(filters.stopDateTime).subtract(1, 'week')
    )
    setLastDate(date)
    dispatch(setCalendarLastDate({ date }))
    if (!isInRange) {
      onFilterChange({
        type: 'change',
        params: {
          startDateTime: moment(date).startOf('month').subtract(1, 'week'),
          stopDateTime: moment(date).endOf('month').add(1, 'week'),
          resultMax: MAX_PER_CALENDAR_PAGE,
        },
      })
    }
  }

  const handleSelectEvent = (data) => {
    toggleActiveEvent(data.id)
  }

  const handleViewChange = (view) => {
    dispatch(setCalendarViewType({ type: view }))
    setCurrentView(view)
  }

  const components = {
    // day: {
    //   event: DayEvent,
    //   header: EventHeader
    // },
    // day: DayComponent,
    eventWrapper: (props) => {
      return <EventWrapper {...props} getEvents={() => events} />
    },
    toolbar: (props) => (
      <Toolbar {...props} loading={loading} currentView={currentView} />
    ),
  }
  const views = 
  {
    month: true,
    week: true,
    work_week: true,
    day: true,
    agenda: ListView
  }

  if (loading && currentView === 'agenda') {
    return (
      <LoadingToolbar
        handleNavigation={handleNavigation}
        handleViewChange={handleViewChange}
        currentView={currentView}
        lastDate={lastDate}
        views={views}
      />
    )
  }

  return (
    <div ref={calendarRef} style={offsetHeightStyles}>
      {didFirstRender ? <Calendar
        resizable={false}
        onSelectEvent={handleSelectEvent}
        onNavigate={handleNavigation}
        localizer={localizer}
        events={calendarEvents}
        onView={handleViewChange}
        defaultView={currentView}
        defaultDate={lastDate}
        components={components}
        dayLayoutAlgorithm='no-overlap'
        step={15} // minutes per time slot
        views={views}
      /> : null}
    </div>
  )
}

const LoadingToolbar = props => {
  const { handleNavigation, handleViewChange, currentView, lastDate, views } = props
  const mockToolbarProps = {
    localizer: {
      messages: {
        today: 'Today',
        agenda: 'Agenda',
        month: 'Month',
        week: 'Week',
        work_week: 'Work Week',
        day: 'Day',
      },
    },
    label: 'Loading...',
    date: lastDate,
    loading: true,
    currentView,
    views,
    onNavigate: handleNavigation,
    onView: handleViewChange,
  }
  return (
    <Toolbar {...mockToolbarProps} loading={true} currentView={currentView} />
  )
}

export default CalendarView
